import React, { useState, useEffect, useCallback } from 'react';
import API from 'shared/api';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiDollarSign } from 'react-icons/fi';
import { C } from 'shared/darkColors';

const EMPTY_FORM = { id: null, nombre: '', precio_mensual_sede_usd: '', max_sedes: '' };

export default function Planes() {
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  const fetchPlanes = useCallback(async () => {
    try {
      const r = await API.get('/planes/listar');
      if (r.data.status === 'success') setPlanes(r.data.data);
    } catch (e) {
      console.error('Error cargando planes:', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlanes();
  }, [fetchPlanes]);

  const openModal = (p = null) => {
    if (p) {
      setForm({
        id: p.id,
        nombre: p.nombre || '',
        precio_mensual_sede_usd: p.precio_mensual_sede_usd || '',
        max_sedes: p.max_sedes || '',
      });
      setModal('edit');
    } else {
      setForm(EMPTY_FORM);
      setModal('new');
    }
  };

  const save = async () => {
    if (!form.nombre || form.nombre.trim().length < 2)
      return showToast('Nombre requerido (mínimo 2 caracteres)', false);
    if (!form.precio_mensual_sede_usd || parseFloat(form.precio_mensual_sede_usd) <= 0)
      return showToast('Precio debe ser mayor a 0', false);
    if (form.max_sedes && parseInt(form.max_sedes, 10) < 1)
      return showToast('Maximo de sedes debe ser al menos 1', false);
    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('nombre', form.nombre.trim());
      params.append('precio_mensual_sede_usd', form.precio_mensual_sede_usd);
      params.append('max_sedes', form.max_sedes || '');
      if (modal === 'new') {
        await API.post(`/planes/agregar?${params.toString()}`);
        showToast('Plan creado');
      } else {
        await API.put(`/planes/modificar/${form.id}?${params.toString()}`);
        showToast('Plan actualizado');
      }
      setModal(null);
      fetchPlanes();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Error al guardar';
      showToast(msg, false);
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Eliminar este plan?')) return;
    try {
      await API.delete(`/planes/eliminar/${id}`);
      showToast('Plan eliminado');
      fetchPlanes();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Error al eliminar';
      showToast(msg, false);
    }
  };

  if (loading) return <div style={C.loading}>Cargando...</div>;

  return (
    <div className="sa-pl-wrapper" style={C.wrapper}>
      <style>{`@keyframes toastIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 768px) {
          .sa-pl-wrapper { padding: 16px 12px !important; }
          .sa-pl-section { padding: 18px !important; border-radius: 12px !important; }
          .sa-pl-section-head { flex-direction: column; gap: 12px; align-items: flex-start; }
          .sa-pl-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .sa-pl-table-wrap table { min-width: 500px; }
          .sa-pl-modal-box { max-width: 95vw !important; padding: 24px !important; max-height: 90vh !important; border-radius: 16px !important; }
        }
      `}</style>
      <div style={C.topBar}>
        <div>
          <div style={C.title}>Planes</div>
          <div style={C.subtitle}>Gestion de precios por cantidad de sedes</div>
        </div>
      </div>

      <div className="sa-pl-section" style={C.section}>
        <div className="sa-pl-section-head" style={C.sectionHead}>
          <div style={C.sectionTitle}>Planes ({planes.length})</div>
          <button onClick={() => openModal()} style={C.btnPrimary}>
            <FiPlus size={14} /> Nuevo
          </button>
        </div>
        <div className="sa-pl-table-wrap">
          <table style={C.table}>
            <thead>
              <tr>
                <th style={C.th}>Nombre</th>
                <th style={C.th}>Max Sedes</th>
                <th style={C.th}>Precio Mensual</th>
                <th style={C.th}>Activo</th>
                <th style={C.th}></th>
              </tr>
            </thead>
            <tbody>
              {planes.length === 0 ? (
                <tr>
                  <td colSpan={5} style={C.emptyRow}>
                    No hay planes registrados
                  </td>
                </tr>
              ) : (
                planes.map((p) => (
                  <tr key={p.id}>
                    <td style={{ ...C.td, fontWeight: 600 }}>{p.nombre}</td>
                    <td style={C.td}>
                      {p.max_sedes || <span style={{ color: '#9ca3af' }}>Ilimitado</span>}
                    </td>
                    <td style={{ ...C.td, fontWeight: 600, color: '#059669' }}>
                      <FiDollarSign size={12} style={{ marginRight: '3px' }} />
                      {p.precio_mensual_sede_usd}
                    </td>
                    <td style={C.td}>
                      <span
                        style={{ ...C.badge, ...(p.activo !== false ? C.badgeGreen : C.badgeRed) }}
                      >
                        {p.activo !== false ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={C.td}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => openModal(p)}
                          style={{ ...C.btnSm, background: '#eff6ff', color: '#2563eb' }}
                        >
                          <FiEdit2 size={12} />
                        </button>
                        <button onClick={() => deletePlan(p.id)} style={C.btnDanger}>
                          <FiTrash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div style={C.modal} onClick={() => setModal(null)}>
          <div className="sa-pl-modal-box" style={C.modalBox} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
              }}
            >
              <div style={C.modalTitle}>{modal === 'edit' ? 'Editar Plan' : 'Nuevo Plan'}</div>
              <button
                onClick={() => setModal(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af',
                  padding: '4px',
                  borderRadius: '8px',
                }}
              >
                <FiX size={20} />
              </button>
            </div>
            <div style={C.formGroup}>
              <label style={C.formLabel}>Nombre</label>
              <input
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                style={C.formInput}
                placeholder="Ej: 1 Sede"
              />
            </div>
            <div style={C.formGroup}>
              <label style={C.formLabel}>Maximo de sedes</label>
              <input
                value={form.max_sedes}
                onChange={(e) => setForm({ ...form, max_sedes: e.target.value })}
                style={C.formInput}
                type="number"
                placeholder="Ej: 3"
              />
            </div>
            <div style={C.formGroup}>
              <label style={C.formLabel}>Precio mensual (USD)</label>
              <input
                value={form.precio_mensual_sede_usd}
                onChange={(e) => setForm({ ...form, precio_mensual_sede_usd: e.target.value })}
                style={C.formInput}
                type="number"
                placeholder="39"
              />
            </div>
            <div style={C.formActions}>
              <button onClick={() => setModal(null)} style={C.btnOutline}>
                Cancelar
              </button>
              <button
                onClick={save}
                disabled={saving}
                style={{ ...C.btnPrimary, opacity: saving ? 0.6 : 1 }}
              >
                <FiSave size={14} /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ ...C.toast, background: toast.ok ? '#111827' : '#dc2626' }}>{toast.msg}</div>
      )}
    </div>
  );
}
